'use client';

import {
    MetricInfoMap,
    SORT_ASC,
    SORT_COLUMN_MEDIAN,
    SORT_COLUMN_P10,
    SORT_COLUMN_P90,
    SORT_COLUMN_P99,
    SORT_COLUMN_STD_DEV,
    SORT_DESC,
} from '@/constants/broker_metrics';
import { useActiveTabBrokerStatsMap, useBrokerByCode } from '@/hooks/useBrokerMetricsSelectors';
import type { SortColumn, SortDirection } from '@/types/main';
import { computeBrokerStats, sortBrokerStats } from '@/utils/convert';
import { useMemo, useState } from 'react';
import BrokerLogo from './UI/BrokerLogo';
import InfoCircleIcon from './UI/InfoCircleIcon';
import SortableHeader from './UI/SortableHeader';

function MetricCell({ valueMs, lastColumn = false }: Readonly<{ valueMs: number | null; lastColumn?: boolean }>) {
    return (
        <td className={`py-4 text-right text-xs font-semibold leading-5 text-[#3A3A3A] ${lastColumn ? 'pl-2 pr-4' : 'px-2'}`}>
            {valueMs === null ? '—' : Math.round(valueMs)}
        </td>
    );
}

function MobileMetric({
    label,
    info,
    valueMs,
}: Readonly<{
    label: string;
    info: string;
    valueMs: number | null;
}>) {
    return (
        <div className='flex flex-col gap-0.5'>
            <div className='flex items-center gap-0.5'>
                <button type='button' className='tooltip tooltip-bottom inline-flex' data-tip={info}>
                    <InfoCircleIcon />
                </button>
                <span className='whitespace-nowrap text-xs leading-4 text-[#808080]'>{label}</span>
            </div>
            <p className='whitespace-nowrap text-xs font-medium leading-4 text-[#2A2A2A]'>{valueMs === null ? '—' : `${Math.round(valueMs)} ms`}</p>
        </div>
    );
}

function BrokerStatsTable() {
    const brokerByCode = useBrokerByCode();
    const brokerStatsMap = useActiveTabBrokerStatsMap();
    const stats = useMemo(() => computeBrokerStats(brokerStatsMap), [brokerStatsMap]);

    const [sortColumn, setSortColumn] = useState<SortColumn>(SORT_COLUMN_MEDIAN);
    const [sortDirection, setSortDirection] = useState<SortDirection>(SORT_ASC);

    const handleSort = (column: SortColumn) => {
        if (column === sortColumn) {
            setSortDirection((prev) => (prev === SORT_ASC ? SORT_DESC : SORT_ASC));
        } else {
            setSortColumn(column);
            setSortDirection(SORT_ASC);
        }
    };

    const rows = useMemo(() => sortBrokerStats(stats, sortColumn, sortDirection), [stats, sortColumn, sortDirection]);

    if (stats.length === 0) {
        return null;
    }

    return (
        <div className='mt-6 flex flex-col gap-3 md:mt-24 md:gap-2'>
            <div className='flex flex-col gap-1'>
                <h3 className='text-base font-medium leading-6 text-[#2A2A2A] md:text-lg md:font-semibold md:leading-normal md:text-inherit'>
                    Overall Stats
                </h3>
                <p className='text-xs leading-4 text-[#555] md:leading-normal md:text-slate-500'>
                    <span className='md:hidden'>Key performance metrics across all brokers</span>
                    <span className='hidden md:inline'>Click a column to sort brokers by each metric.</span>
                </p>
            </div>

            <div className='overflow-hidden rounded-xl border border-[#F3F3F3] bg-white md:hidden'>
                <ul>
                    {rows.map((row) => (
                        <li key={row.broker} className='border-b border-[#F3F3F3] p-4 last:border-b-0'>
                            <div className='flex flex-col gap-4'>
                                <div className='flex h-8 items-center gap-2'>
                                    <BrokerLogo broker={row.displayName} logoUrl={brokerByCode.get(row.broker)?.logo_url} size={32} rounded='md' />
                                    <span className='text-sm font-medium leading-5 text-[#3A3A3A]'>{row.displayName}</span>
                                </div>
                                <div className='flex items-center justify-between'>
                                    <MobileMetric label='Median' info={MetricInfoMap.median} valueMs={row.median.valueMs} />
                                    <MobileMetric label='P90' info={MetricInfoMap.p90} valueMs={row.p90.valueMs} />
                                    <MobileMetric label='Peak' info={MetricInfoMap.p99} valueMs={row.p99.valueMs} />
                                    <MobileMetric label='STD DEV' info={MetricInfoMap.stdDev} valueMs={row.stdDev.valueMs} />
                                </div>
                            </div>
                        </li>
                    ))}
                </ul>
            </div>

            <div className='hidden w-full overflow-x-hidden border-t border-[#E3E3E3] bg-white md:block'>
                <table className='w-full min-w-0 table-fixed'>
                    <thead>
                        <tr className='border-b border-[#E3E3E3] bg-[#FAFAFA]'>
                            <th scope='col' className='w-[24%] py-2 pl-4 pr-2 text-left text-xs font-medium leading-4 text-[#3A3A3A]'>
                                Broker
                            </th>
                            <SortableHeader
                                label='Median'
                                column={SORT_COLUMN_MEDIAN}
                                info={MetricInfoMap.median}
                                padding='px-2 py-2'
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SortableHeader
                                label='P90'
                                column={SORT_COLUMN_P90}
                                info={MetricInfoMap.p90}
                                padding='px-2 py-2'
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SortableHeader
                                label='P99'
                                column={SORT_COLUMN_P99}
                                info={MetricInfoMap.p99}
                                padding='px-2 py-2'
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SortableHeader
                                label='P10'
                                column={SORT_COLUMN_P10}
                                info={MetricInfoMap.p10}
                                padding='px-2 py-2'
                                tooltipPosition='left'
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                            <SortableHeader
                                label='STD DEV'
                                column={SORT_COLUMN_STD_DEV}
                                info={MetricInfoMap.stdDev}
                                padding='py-2 pl-2 pr-4'
                                tooltipPosition='left'
                                sortColumn={sortColumn}
                                sortDirection={sortDirection}
                                onSort={handleSort}
                            />
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row) => {
                            return (
                                <tr key={row.broker} className='border-b border-[#E3E3E3] bg-white'>
                                    <td className='py-4 pl-4 pr-2'>
                                        <span className='flex min-w-0 items-center gap-2'>
                                            <BrokerLogo
                                                broker={row.displayName}
                                                logoUrl={brokerByCode.get(row.broker)?.logo_url}
                                                size={32}
                                                rounded='md'
                                            />
                                            <span className='truncate text-sm font-medium leading-5 text-[#3A3A3A]'>{row.displayName}</span>
                                        </span>
                                    </td>
                                    <MetricCell valueMs={row.median.valueMs} />
                                    <MetricCell valueMs={row.p90.valueMs} />
                                    <MetricCell valueMs={row.p99.valueMs} />
                                    <MetricCell valueMs={row.p10.valueMs} />
                                    <MetricCell valueMs={row.stdDev.valueMs} lastColumn />
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

export default BrokerStatsTable;
